import React, { useEffect, useState } from 'react';
import Slider from 'rc-slider';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useService } from '../../../containers/UseService';
import { useAuth } from '../../../containers/UseAuth';
import { EXPECTED_STATS_UI_ENABLED } from '../../../constants/constants';
import {
    dashboardActions,
    notificationsActions,
    screensActions,
} from '../../../store';
import CommonButton from '../../Buttons/CommonButton';
import SwitcherButton from '../../Buttons/CommonSwitcher';
import CurrencyText from '../../Formats/CurrencyText';
import { canAccessAdminFeatures } from '../../../utils/authPermissions';
import { campaignStatus, formatNumber } from '../../../utils/functions';
import ExpectedStatsSyncModal from './ExpectedStatsSyncModal';
import styles from './QuickControls.module.css';

const QuickControls = () => {
    const { t } = useTranslation();
    const service = useService();
    const auth = useAuth();
    const dispatch = useDispatch();
    const dashboard = useSelector((state) => {
        return state.dashboard;
    });
    const campaign = dashboard.campaign;
    const isActive = campaign.status === 5;
    const id = campaign.id;
    const isActiveOrInactive =
        campaign.status === campaignStatus('ACTIVE') ||
        campaign.status === campaignStatus('INACTIVE');
    const isCpmOptimized = campaign['cpm_optimize'] === 1;
    const campaignBudget =
        campaign['budget'] !== null && campaign['budget'] !== undefined
            ? parseFloat(campaign['budget'])
            : 0;
    const isDailyCapByBudget = !!campaign?.limit_type;
    const isDailyCapEnabled = !(
        campaign['auto_distribution'] === 0 &&
        parseFloat(campaign['daily_limit']) === 0
    );
    const isDailyCapAutoDistributed =
        parseFloat(campaign['auto_distribution']) === 1;
    const [cpmSliderValue, setCpmSliderValue] = useState(0);
    const [dailyCapLimitSliderValue, setDailyCapLimitSliderValue] = useState(0);
    const [isSyncingExpectedStats, setIsSyncingExpectedStats] = useState(false);
    const [isSavingExpectedStats, setIsSavingExpectedStats] = useState(false);
    const [isExpectedStatsModalOpen, setIsExpectedStatsModalOpen] =
        useState(false);
    const [expectedStatsPreview, setExpectedStatsPreview] = useState(null);
    const history = useHistory();
    const canSyncExpectedStats =
        (auth.isAdmin() || auth.isSupervising()) && EXPECTED_STATS_UI_ENABLED;
    const canAuditCampaign = isActive && canAccessAdminFeatures(auth);
    const expectedStatsTargets = campaign.expected_stats_targets || {};
    const hasExpectedStatsTargets = Object.values(expectedStatsTargets).some(
        (metrics) =>
            metrics &&
            ['impressions', 'impacts', 'spent'].some(
                (metricKey) =>
                    metrics[metricKey] !== null &&
                    metrics[metricKey] !== undefined
            )
    );
    const canApplyExpectedStatsSync =
        canSyncExpectedStats &&
        hasExpectedStatsTargets &&
        campaign.status === campaignStatus('JOB_SUCCESS');

    const toggleActiveCampaign = async () => {
        try {
            const campaignCopy = JSON.parse(JSON.stringify(campaign));
            dispatch(dashboardActions.setIsLoading(true));
            if (isActive) {
                campaignCopy.status = 'ACTIVE';
                await service.deactivateCampaign(campaignCopy);
            } else {
                campaignCopy.status = 'INACTIVE';
                await service.activateCampaign(campaignCopy);
            }
            await service.getCampaignPreview(campaign.id);
            dispatch(dashboardActions.setIsLoading(false));
        } catch (error) {
            console.log(error);
        }
    };

    const editCampaign = () => {
        dispatch(screensActions.resetByKey('maxCpm'));
        dispatch(screensActions.resetByKey('selectedScreensLocal'));
        dispatch(screensActions.resetByKey('isFilterByAudiences'));
        dispatch(screensActions.resetByKey('showSelectedScreens'));
        dispatch(screensActions.resetByKey('selectedScreenTypes'));
        dispatch(screensActions.resetByKey('selectedPublishers'));
        dispatch(screensActions.resetByKey('selectedTiers'));
        history.push(`/campaign/edit/${id}`);
    };

    const openExpectedStatsManager = () => {
        setExpectedStatsPreview(null);
        setIsExpectedStatsModalOpen(true);
    };

    const closeExpectedStatsModal = () => {
        if (!isSyncingExpectedStats && !isSavingExpectedStats) {
            setIsExpectedStatsModalOpen(false);
            setExpectedStatsPreview(null);
        }
    };

    const saveExpectedStatsTargets = async (editedTargets) => {
        try {
            setIsSavingExpectedStats(true);
            await service.saveCampaignExpectedStatsTargets({
                campaign,
                expectedStats: editedTargets,
            });
            await service.getCampaignPreview(campaign.id);

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.saveSuccess'
                    ),
                    message: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.saveDescription'
                    ),
                })
            );

            setIsExpectedStatsModalOpen(false);
            setExpectedStatsPreview(null);
        } catch (error) {
            console.error(error);
            dispatch(
                notificationsActions.setNotification({
                    type: 'error',
                    title: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.saveError'
                    ),
                    message: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.saveErrorDescription'
                    ),
                })
            );
        } finally {
            setIsSavingExpectedStats(false);
        }
    };

    const syncExpectedStats = async () => {
        if (!canApplyExpectedStatsSync || isSyncingExpectedStats) {
            return;
        }

        try {
            setIsSyncingExpectedStats(true);
            const dryRunResult = await service.adjustCampaignExpectedStats({
                campaignId: campaign.id,
                expectedStats: expectedStatsTargets,
                apply: false,
            });

            setExpectedStatsPreview(dryRunResult);
            setIsExpectedStatsModalOpen(true);
        } catch (error) {
            console.error(error);
            dispatch(
                notificationsActions.setNotification({
                    type: 'error',
                    title: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.error'
                    ),
                    message: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.errorDescription'
                    ),
                })
            );
        } finally {
            setIsSyncingExpectedStats(false);
        }
    };

    const confirmExpectedStatsSync = async (editedTargets) => {
        if (!expectedStatsPreview || isSyncingExpectedStats) {
            return;
        }

        try {
            setIsSyncingExpectedStats(true);
            await service.saveCampaignExpectedStatsTargets({
                campaign,
                expectedStats: editedTargets,
            });
            const applyResult = await service.adjustCampaignExpectedStats({
                campaignId: campaign.id,
                expectedStats: editedTargets,
                apply: true,
            });

            setIsExpectedStatsModalOpen(false);
            setExpectedStatsPreview(null);
            await service.getCampaignPreview(campaign.id);

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.success'
                    ),
                    message: [
                        `${t(
                            'sections.dashboard-individual.quick-controls.expected-stats.changedRows'
                        )}: ${applyResult?.changedRows ?? 0}`,
                        `${t(
                            'sections.dashboard-individual.quick-controls.expected-stats.missingScreens'
                        )}: ${applyResult?.missingTargetScreens?.length ?? 0}`,
                    ].join(' · '),
                })
            );
        } catch (error) {
            console.error(error);
            dispatch(
                notificationsActions.setNotification({
                    type: 'error',
                    title: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.error'
                    ),
                    message: t(
                        'sections.dashboard-individual.quick-controls.expected-stats.errorDescription'
                    ),
                })
            );
        } finally {
            setIsSyncingExpectedStats(false);
        }
    };

    const handleChangeDailyCapLimit = async (newDailyCapLimit) => {
        await service.changeDailyCapLimit(campaign.id, newDailyCapLimit);
        service.getCampaignPreview(campaign.id);
    };

    const handleChangeCpm = async (newCpm) => {
        await service.changeCpm(campaign.id, newCpm);
        service.getCampaignPreview(campaign.id);
    };

    useEffect(() => {
        const campaignCpm =
            campaign['max_cpm'] !== null && campaign['max_cpm'] !== undefined
                ? parseFloat(campaign['max_cpm'])
                : 0;
        const campaignDailyLimit =
            campaign['daily_limit'] !== null &&
            campaign['daily_limit'] !== undefined
                ? campaign.budget - campaign.daily_limit > 0
                    ? parseFloat(campaign['daily_limit'])
                    : parseFloat(campaign['budget'])
                : 0;
        setCpmSliderValue(campaignCpm);
        setDailyCapLimitSliderValue(campaignDailyLimit);
    }, [campaign]);

    return (
        <>
            <ExpectedStatsSyncModal
                active={isExpectedStatsModalOpen}
                toggler={closeExpectedStatsModal}
                onSave={saveExpectedStatsTargets}
                onConfirmSync={confirmExpectedStatsSync}
                isSaving={isSavingExpectedStats}
                isApplying={isSyncingExpectedStats}
                campaign={campaign}
                syncPreview={expectedStatsPreview}
                screenStats={dashboard?.individualCampaignStats?.screen || []}
            />
            <div className="h-5/6 w-8 pointer-events-none absolute top-1 left-1 ml-2 mt-1 z-10 bg-gradient-to-r from-white"></div>
            <div className="h-5/6 w-8 pointer-events-none absolute top-1 right-1 mt-1 z-10 bg-gradient-to-l from-white"></div>
            <div className="w-full flex justify-between items-start overflow-x-auto gap-2">
                <div className="ml-2 font-light text-xs uppercase">
                    {t('sections.dashboard-individual.quick-controls.title')}
                </div>
                <div className="mr-2 self-center flex gap-2">
                    {canAuditCampaign && (
                        <CommonButton
                            type="primaryButton"
                            styleType="secondary"
                            onClick={() =>
                                history.push(`/dashboard/${id}/audit`)
                            }>
                            <span>
                                {t(
                                    'sections.dashboard-individual.quick-controls.audit-button'
                                )}
                            </span>
                        </CommonButton>
                    )}
                    {canSyncExpectedStats && (
                        <CommonButton
                            type="primaryButton"
                            styleType="secondary"
                            disabled={isSavingExpectedStats}
                            onClick={openExpectedStatsManager}>
                            <span>
                                {t(
                                    'sections.dashboard-individual.quick-controls.expected-stats.manageButton'
                                )}
                            </span>
                        </CommonButton>
                    )}
                    {canSyncExpectedStats && (
                        <CommonButton
                            type="primaryButton"
                            styleType="secondary"
                            disabled={
                                !canApplyExpectedStatsSync ||
                                isSyncingExpectedStats ||
                                isSavingExpectedStats
                            }
                            onClick={syncExpectedStats}>
                            <span>
                                {isSyncingExpectedStats
                                    ? t(
                                          'sections.dashboard-individual.quick-controls.expected-stats.syncing'
                                      )
                                    : t(
                                          'sections.dashboard-individual.quick-controls.expected-stats.button'
                                      )}
                            </span>
                        </CommonButton>
                    )}
                    <CommonButton
                        type="primaryButton"
                        styleType="smallprimary"
                        onClick={editCampaign}>
                        <span>
                            {t(
                                'sections.campaign-list.card-campaign.edit-dashboard'
                            )}
                        </span>
                    </CommonButton>
                </div>
            </div>
            {canSyncExpectedStats && (
                <div className="px-2 pt-2 text-xs text-gray-500">
                    {!hasExpectedStatsTargets &&
                        t(
                            'sections.dashboard-individual.quick-controls.expected-stats.noTargets'
                        )}
                    {hasExpectedStatsTargets &&
                        !canApplyExpectedStatsSync &&
                        t(
                            'sections.dashboard-individual.quick-controls.expected-stats.notReady'
                        )}
                </div>
            )}
            <div className="flex h-3/5 justify-between self-center content-center items-center flex-nowrap divide-x divide-dotted mt-2 px-2 overflow-x-auto overflow-y-hidden">
                <div
                    className={`mr-2 self-center ${
                        isActiveOrInactive ? 'block' : 'hidden'
                    }`}>
                    <div className="w-24 mt-2 ml-2">
                        <SwitcherButton
                            onToggle={toggleActiveCampaign}
                            status={isActive}
                            labelOn={t(
                                'sections.dashboard-individual.quick-controls.campaign-active'
                            )}
                            labelOff={t(
                                'sections.dashboard-individual.quick-controls.campaign-paused'
                            )}
                            revertLabel={true}
                        />
                    </div>
                </div>

                <div
                    className={`mx-2 flex-1 w-40 px-2 ${
                        (isCpmOptimized || campaignBudget === 0) && 'hidden'
                    }`}>
                    <div className="flex">
                        <div>
                            <span className="text-gray-400 text-xs font-light">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-basics.budget.cpm-label'
                                )}
                            </span>
                            <div className="flex">
                                <div style={{ minWidth: '9ch' }}>
                                    <CurrencyText
                                        currency={
                                            campaign?.currency?.short_name
                                        }
                                        value={cpmSliderValue}
                                        textSize="text-md"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="ml-2 mt-2">
                            <div className={styles.sliders}>
                                <Slider
                                    vertical={true}
                                    min={1}
                                    max={
                                        parseFloat(campaign.max_cpm) > 0
                                            ? parseInt(campaign.max_cpm) === 1
                                                ? 100
                                                : parseInt(campaign.max_cpm) *
                                                  1.5
                                            : parseFloat(campaign['max_cpm'])
                                    }
                                    value={cpmSliderValue}
                                    onChange={setCpmSliderValue}
                                    onAfterChange={handleChangeCpm}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className={`mx-2  flex-1 w-40 px-2 ${
                        (!isDailyCapEnabled ||
                            isDailyCapAutoDistributed ||
                            campaignBudget === 0) &&
                        'hidden'
                    }`}>
                    <div className="flex">
                        <div>
                            <span className="text-gray-400 text-xs font-light">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-basics.dailyCap.title'
                                )}
                            </span>
                            <div className="flex">
                                {isDailyCapByBudget ? (
                                    <div style={{ minWidth: '9ch' }}>
                                        <CurrencyText
                                            currency={
                                                campaign?.currency?.short_name
                                            }
                                            value={dailyCapLimitSliderValue}
                                            textSize="text-md"
                                        />
                                    </div>
                                ) : (
                                    <div className="font-semibold text-md">
                                        <span className="text-xs font-thin text-gray-500">
                                            {t(
                                                'sections.campaigns.new-campaign.wizard-briefBar.basics.abbreviated-impressions'
                                            )}
                                        </span>{' '}
                                        {formatNumber(
                                            campaign.daily_limit,
                                            'rounded'
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="ml-2 mt-2">
                            <div className={styles.sliders}>
                                <Slider
                                    vertical={true}
                                    min={1}
                                    max={
                                        campaign.budget - campaign.daily_limit >
                                        0
                                            ? parseInt(campaign.daily_limit) ===
                                              1
                                                ? 100
                                                : parseInt(
                                                      campaign.daily_limit
                                                  ) * 1.5
                                            : parseFloat(campaign['budget'])
                                    }
                                    value={dailyCapLimitSliderValue}
                                    defaultValue={dailyCapLimitSliderValue}
                                    onAfterChange={handleChangeDailyCapLimit}
                                    onChange={setDailyCapLimitSliderValue}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default QuickControls;
